import React, { useEffect, useState } from 'react';
import { User } from '../../../../types';
import {
  CLASSROOM_TEAM_COLORS,
  ClassroomTeam,
  PortfolioEntry,
  teamSessionStatus,
} from '../../classroom/types';
import { ClassroomTeamsService } from '../../classroom/ClassroomTeamsService';
import ClassroomStudentChip from './ClassroomStudentChip';
import AvatarPickerModal from './AvatarPickerModal';
import { ClassroomAvatarService } from '../../classroom/ClassroomAvatarService';

interface TeamCardProps {
  team: ClassroomTeam;
  studentsById: Record<string, User>;
  squadId: string;
  entries: PortfolioEntry[];
  levelId: string;
  attendance?: Record<string, boolean>;
  readOnly?: boolean;
  dragOver?: boolean;
  onDragOverChange: (teamId: string | null) => void;
  onOpenPortfolio: (teamId: string) => void;
  onRename: (team: ClassroomTeam) => void;
  onUploadPhotos: (teamId: string) => void;
  teams: ClassroomTeam[];
}

const TeamCard: React.FC<TeamCardProps> = ({
  team,
  studentsById,
  squadId,
  entries,
  levelId,
  attendance,
  readOnly,
  dragOver,
  onDragOverChange,
  onOpenPortfolio,
  onRename,
  onUploadPhotos,
  teams,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [avatarOpen,setAvatarOpen]=useState(false),[avatarUrl,setAvatarUrl]=useState(team.avatarUrl || '');
  useEffect(()=>setAvatarUrl(team.avatarUrl || ''),[team.avatarUrl]);
  const saveAvatar=async(url:string,kind:'muse'|'upload')=>{setBusy(true);try{await ClassroomAvatarService.setTeam(squadId,team.id,url,kind);setAvatarUrl(url);setAvatarOpen(false);}finally{setBusy(false);}};
  const colors = CLASSROOM_TEAM_COLORS[team.colorKey] || CLASSROOM_TEAM_COLORS.reef;
  const status = teamSessionStatus(entries, team.id, levelId);
  const members = team.memberIds
    .map((id) => studentsById[id])
    .filter(Boolean) as User[];

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    onDragOverChange(null);
    if (readOnly) return;
    const studentId = e.dataTransfer.getData('text/plain');
    if (!studentId) return;
    setBusy(true);
    try {
      await ClassroomTeamsService.assignStudent({
        squadId,
        studentId,
        toTeamId: team.id,
        teams,
      });
    } catch (err) {
      console.error('Failed to assign student', err);
      void window.appAlert('Could not assign student to this team.');
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async () => {
    setMenuOpen(false);
    if (!await window.appConfirm(`Archive “${team.name}”? Portfolio work is kept.`)) return;
    try {
      await ClassroomTeamsService.archiveTeam(squadId, team.id);
    } catch (err) {
      console.error(err);
      void window.appAlert('Could not archive team.');
    }
  };

  return (
    <div
      className={`cd-team ${dragOver ? 'is-drop-target' : ''} ${busy ? 'is-busy' : ''}`}
      style={{ borderLeftColor: colors.accent }}
      onDragOver={(e) => {
        if (readOnly) return;
        e.preventDefault();
        onDragOverChange(team.id);
      }}
      onDragLeave={() => onDragOverChange(null)}
      onDrop={handleDrop}
    >
      <div className="cd-team-row">
        <div className="cd-team-identity">
          <button type="button" className="cd-team-avatar" onClick={()=>setAvatarOpen(true)} aria-label={`Choose ${team.name} avatar`}>{avatarUrl?<img src={avatarUrl} alt=""/>:<span>＋</span>}</button>
          <button
            type="button"
            className="cd-team-name cd-team-name-edit"
            onClick={() => onRename(team)}
            disabled={readOnly}
            title={readOnly ? 'Team names are locked for completed classes' : 'Rename team'}
          >
            {team.name}<span aria-hidden="true">✎</span>
          </button>
        </div>
        <div className="cd-team-menu-wrap">
          <button
            type="button"
            className="cd-team-menu-btn"
            aria-label="Team menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            ···
          </button>
          {menuOpen && (
            <div className="cd-team-menu">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onRename(team);
                }}
                disabled={readOnly}
              >
                Rename
              </button>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onOpenPortfolio(team.id);
                }}
              >
                Portfolio
              </button>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onUploadPhotos(team.id);
                }}
              >
                Add photos
              </button>
              <button type="button" className="is-danger" onClick={handleArchive} disabled={readOnly}>
                Archive team
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="cd-team-members">
        {members.length === 0 ? (
          <span className="cd-tray-empty">Drop students here</span>
        ) : (
          members.map((s) => (
            <ClassroomStudentChip
              key={s.id}
              student={s}
              squadId={squadId}
              variant="member"
              draggable={!readOnly}
              absent={attendance?.[s.id] === false}
            />
          ))
        )}
      </div>

      <div className="cd-team-stat">
        {status.label === 'none' && <span className="cd-pill cd-pill-warn">No photos yet</span>}
        {status.label === 'photos' && (
          <span className="cd-pill cd-pill-done">
            {status.photoCount} photo{status.photoCount === 1 ? '' : 's'}
          </span>
        )}
        {status.label === 'ai_ready' && (
          <>
            <span className="cd-pill cd-pill-done">{status.photoCount} photos</span>
            <span className="cd-pill cd-pill-reveal">✦ AI ready</span>
          </>
        )}
        {status.label === 'revealed' && (
          <>
            <span className="cd-pill cd-pill-done">{status.photoCount} photos</span>
            <span className="cd-pill cd-pill-reveal">✦ Revealed</span>
          </>
        )}
        <button
          type="button"
          className="cd-btn cd-btn-compact"
          onClick={() => onUploadPhotos(team.id)}
        >
          📷 Add photos
        </button>
      </div>
      <AvatarPickerModal isOpen={avatarOpen} title={`Choose ${team.name} avatar`} busy={busy} onClose={()=>setAvatarOpen(false)} onMuse={url=>void saveAvatar(url,'muse')} onUpload={file=>void (async()=>{setBusy(true);try{const url=await ClassroomAvatarService.upload(squadId,team.id,file);await saveAvatar(url,'upload');}finally{setBusy(false);}})()} />
    </div>
  );
};

export default TeamCard;
